import { useCallback, useEffect, useState } from "react";

import Chip from "./chip/chip";

const correctInitialFormat = (
  type: ChipFilterProps["type"],
  selected: ChipFilterProps["selected"]
) => {
  if (type === "single") {
    if (typeof selected === "string") {
      return selected;
    } else {
      return selected.length > 0 ? selected[0] : "";
    }
  } else {
    if (typeof selected !== "string") {
      return selected;
    } else {
      return selected.length === 0 ? [] : [selected];
    }
  }
};

export type ChipListItem = {
  label: string;
  value: string;
};

type ChipFilterProps = {
  type: "single" | "multiple";
  selected: string | string[];
  list: ChipListItem[];
  onChange: (selected: string | string[]) => void;
  brandingColor?: "cerise" | "navy" | "grey";
};

const ChipsFilter = ({
  type,
  selected,
  list,
  onChange,
  brandingColor = "cerise",
}: ChipFilterProps) => {
  const [selectedChips, setSelectedChips] = useState<string | string[]>(
    correctInitialFormat(type, selected)
  );

  useEffect(() => {
    setSelectedChips(correctInitialFormat(type, selected));
  }, [type, selected]);

  const handleChipChange = useCallback(
    (value: string, isSelected: boolean) => {
      if (type === "single") {
        if (!isSelected) {
          setSelectedChips("");
          onChange("");
        } else {
          setSelectedChips(value);
          onChange(value);
        }
      } else {
        if (!isSelected) {
          const newList = (selectedChips as string[]).filter(
            (el) => el !== value
          );
          setSelectedChips(newList);
          onChange(newList);
        } else {
          const newList = [...(selectedChips as string[]), value];
          setSelectedChips(newList);
          onChange(newList);
        }
      }
    },
    [type, selectedChips, setSelectedChips, onChange]
  );

  return (
    <div className={"relative flex"}>
      <div className={"mx-[-20px] overflow-x-auto lg:m-0 lg:overflow-x-hidden"}>
        <ul
          className={
            "flex gap-x-[9px] gap-y-4 whitespace-nowrap md:flex-nowrap lg:flex-wrap"
          }
          aria-label={"Chip filter"}
          role={"listbox"}
          aria-required={"false"}
          aria-disabled={"false"}
          aria-multiselectable={type !== "single"}
        >
          {list.map((listItem, index) => {
            return (
              <li
                key={`${listItem.value}${index}`}
                className={
                  "block first:pl-5 last:pr-5 lg:first:p-0 lg:last:p-0"
                }
                aria-label={"Chip filter button"}
              >
                <Chip
                  {...listItem}
                  isSelected={
                    listItem.value === selectedChips ||
                    selectedChips.includes(listItem.value)
                  }
                  brandingColor={brandingColor}
                  onClick={handleChipChange}
                />
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default ChipsFilter;
